import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
    CustomerRevenueOverviewParams,
    getCustomerRevenueOverview,
} from '../services'

export interface ColumnTotals {
    monthly: Record<
        string,
        {
            allocated_previous_year: number
            allocated_current_year: number
            unallocated: number
        }
    >
    allocatedRenewal: number
    allocatedNewSale: number
    allocatedUpsell: number
    unallocatedRenewal: number
    unallocatedNewSale: number
    unallocatedUpsell: number
}

export const useCustomerRevenueOverview = (
    filters: CustomerRevenueOverviewParams
) => {
    const { id } = useParams()
    const customerId = Number(id)

    const { data: overviewData, isLoading } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.CUSTOMER_REVENUE_COMBINED,
            'overview',
            customerId,
            filters,
        ],
        queryFn: () => getCustomerRevenueOverview(customerId, filters),
        enabled: !!customerId && !!filters.gte && !!filters.lte,
        staleTime: 5 * 60 * 1000,
    })

    const { productGroups, months, grandTotal } = useMemo(() => {
        if (!overviewData) {
            return { productGroups: [], months: [], grandTotal: 0 }
        }
        return {
            productGroups: overviewData.product_groups,
            months: overviewData.months,
            grandTotal: overviewData.grand_total,
        }
    }, [overviewData])

    // Calculate totals for each column
    const columnTotals = useMemo<ColumnTotals>(() => {
        const monthly: ColumnTotals['monthly'] = {}
        let allocatedRenewal = 0
        let allocatedNewSale = 0
        let allocatedUpsell = 0
        let unallocatedRenewal = 0
        let unallocatedNewSale = 0
        let unallocatedUpsell = 0

        months.forEach((month) => {
            monthly[month] = {
                allocated_previous_year: 0,
                allocated_current_year: 0,
                unallocated: 0,
            }
        })

        productGroups.forEach((group) => {
            months.forEach((month) => {
                const monthData = group.monthly_data[month]
                if (monthData) {
                    monthly[month].allocated_previous_year +=
                        monthData.allocated_previous_year
                    monthly[month].allocated_current_year +=
                        monthData.allocated_current_year
                    monthly[month].unallocated += monthData.unallocated
                }
            })
            allocatedRenewal += group.allocated_renewal
            allocatedNewSale += group.allocated_new_sale
            allocatedUpsell += group.allocated_upsell
            unallocatedRenewal += group.unallocated_renewal
            unallocatedNewSale += group.unallocated_new_sale
            unallocatedUpsell += group.unallocated_upsell
        })

        return {
            monthly,
            allocatedRenewal,
            allocatedNewSale,
            allocatedUpsell,
            unallocatedRenewal,
            unallocatedNewSale,
            unallocatedUpsell,
        }
    }, [productGroups, months])

    return {
        productGroups,
        months,
        grandTotal,
        columnTotals,
        isLoading,
    }
}
